import { formatScaled, parseScaled, toSafeNumber } from './decimal.js';
import { CoreError } from './errors.js';

export const CURRENCIES = ['USD', 'KHR', 'VND', 'CNY', 'EUR', 'JPY', 'KRW', 'SGD', 'MYR'] as const;
export type CurrencyCode = (typeof CURRENCIES)[number];

/** Currencies a KHQR, i.e. a Cambodian bank account, can be in. */
export const KHQR_CURRENCIES = ['USD', 'KHR'] as const;
export type KhqrCurrency = (typeof KHQR_CURRENCIES)[number];

export interface CurrencyInfo {
  code: CurrencyCode;
  symbol: string;
  /** Number of fractional digits stored in the minor unit (USD cents = 2, Riel = 0). */
  minorDigits: number;
  symbolPosition: 'prefix' | 'suffix';
}

export const CURRENCY_INFO: Record<CurrencyCode, CurrencyInfo> = {
  USD: { code: 'USD', symbol: '$', minorDigits: 2, symbolPosition: 'prefix' },
  // The Riel has no circulating minor unit, so it is stored as whole riel.
  KHR: { code: 'KHR', symbol: '៛', minorDigits: 0, symbolPosition: 'suffix' },
  VND: { code: 'VND', symbol: '₫', minorDigits: 0, symbolPosition: 'suffix' },
  // "CN¥" and "¥" keep yuan and yen apart; "S$" keeps the Singapore dollar apart from "$".
  CNY: { code: 'CNY', symbol: 'CN¥', minorDigits: 2, symbolPosition: 'prefix' },
  EUR: { code: 'EUR', symbol: '€', minorDigits: 2, symbolPosition: 'prefix' },
  JPY: { code: 'JPY', symbol: '¥', minorDigits: 0, symbolPosition: 'prefix' },
  KRW: { code: 'KRW', symbol: '₩', minorDigits: 0, symbolPosition: 'prefix' },
  SGD: { code: 'SGD', symbol: 'S$', minorDigits: 2, symbolPosition: 'prefix' },
  MYR: { code: 'MYR', symbol: 'RM', minorDigits: 2, symbolPosition: 'prefix' },
};

/**
 * Exchange rates: how many units of each currency one US dollar buys, as
 * decimal strings ("4100", "0.92"). Every conversion goes through the dollar.
 */
export type Rates = Partial<Record<CurrencyCode, string>>;

/**
 * Rough rates used until a group (or the daily update) has better ones.
 * A group's own rates are editable in its settings.
 */
export const DEFAULT_RATES: Record<Exclude<CurrencyCode, 'USD'>, string> = {
  KHR: '4100',
  VND: '25400',
  CNY: '7.1',
  EUR: '0.92',
  JPY: '150',
  KRW: '1380',
  SGD: '1.3',
  MYR: '4.2',
};

/** Default market rate used when a group has not configured its own. */
export const DEFAULT_KHR_PER_USD = Number(DEFAULT_RATES.KHR);

/**
 * Largest amount (in minor units) the system accepts: just under $10 billion
 * or 1 trillion riel, dong, yen or won. Matches the database CHECK constraints and keeps
 * thousands of summed expenses far inside Number.MAX_SAFE_INTEGER.
 */
export const MAX_AMOUNT_MINOR = 1_000_000_000_000 - 1;

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return typeof value === 'string' && (CURRENCIES as readonly string[]).includes(value);
}

export function assertCurrency(value: unknown): CurrencyCode {
  if (!isCurrencyCode(value)) {
    throw new CoreError('INVALID_CURRENCY', `Unsupported currency "${String(value)}"`);
  }
  return value;
}

/** "12.50" USD → 1250. Rejects precision the currency cannot represent. */
export function toMinor(amount: string | number, currency: CurrencyCode): number {
  const minor = toSafeNumber(parseScaled(amount, CURRENCY_INFO[currency].minorDigits));
  if (minor > MAX_AMOUNT_MINOR) {
    throw new CoreError('INVALID_AMOUNT', 'Amount is too large');
  }
  return minor;
}

/** 1250 USD → "12.50"; 50000 KHR → "50000". */
export function fromMinor(minor: number, currency: CurrencyCode): string {
  return formatScaled(minor, CURRENCY_INFO[currency].minorDigits);
}

export interface FormatMoneyOptions {
  /** Prefix positive values with "+" (useful for balances). */
  signed?: boolean;
  /** Drop ".00" on whole-dollar amounts. */
  compact?: boolean;
}

/** 1250 USD → "$12.50", 50000 KHR → "50,000៛", -500 USD → "-$5.00". */
export function formatMoney(
  minor: number,
  currency: CurrencyCode,
  options: FormatMoneyOptions = {},
): string {
  const info = CURRENCY_INFO[currency];
  const absolute = Math.abs(minor);
  const divisor = 10 ** info.minorDigits;
  const hasFraction = absolute % divisor !== 0;
  const digits = options.compact && !hasFraction ? 0 : info.minorDigits;
  const number = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(formatScaled(absolute, info.minorDigits)));
  const body = info.symbolPosition === 'prefix' ? `${info.symbol}${number}` : `${number}${info.symbol}`;
  const sign = minor < 0 ? '-' : options.signed && minor > 0 ? '+' : '';
  return `${sign}${body}`;
}

/** Rates are kept to six decimal places. */
const RATE_DIGITS = 6;
const RATE_ONE = 10n ** BigInt(RATE_DIGITS);
const MAX_RATE = 10_000_000n * RATE_ONE;

/** "4100" or "0.92" → the rate scaled by 10^6. Rejects anything that isn't a sensible positive rate. */
export function parseRate(value: string | number): bigint {
  let scaled: bigint;
  try {
    scaled = parseScaled(value, RATE_DIGITS);
  } catch {
    throw new CoreError('INVALID_EXCHANGE_RATE', 'Exchange rate must be a positive number');
  }
  if (scaled <= 0n || scaled > MAX_RATE) {
    throw new CoreError('INVALID_EXCHANGE_RATE', 'Exchange rate must be a positive number');
  }
  return scaled;
}

/** Whether `value` can be used as a rate ("4100", "0.92"; up to six decimals). */
export function isValidRate(value: unknown): value is string | number {
  if (typeof value !== 'string' && typeof value !== 'number') return false;
  try {
    parseRate(value);
    return true;
  } catch {
    return false;
  }
}

/** Units of `currency` per US dollar (scaled), from `rates` or the defaults. */
function rateOf(rates: Rates, currency: CurrencyCode): bigint {
  if (currency === 'USD') return RATE_ONE;
  return parseRate(rates[currency] ?? DEFAULT_RATES[currency]);
}

/** Every currency's rate per US dollar: the given ones, the defaults for the rest. */
export function fillRates(rates: Rates = {}): Record<Exclude<CurrencyCode, 'USD'>, string> {
  return { ...DEFAULT_RATES, ...Object.fromEntries(Object.entries(rates).filter(([code]) => code !== 'USD')) };
}

/**
 * Converts between any two currencies through the US dollar, rounding half
 * away from zero to the nearest minor unit of the target.
 * `rates` are per dollar; a bare number is the riel rate (whole riel per
 * dollar), as groups have always stored it.
 */
export function convertMinor(minor: number, from: CurrencyCode, to: CurrencyCode, rates: Rates | number): number {
  if (from === to) return minor;
  if (typeof rates === 'number') {
    if (!Number.isInteger(rates) || rates <= 0) {
      throw new CoreError('INVALID_EXCHANGE_RATE', 'Exchange rate must be a positive whole number');
    }
    rates = { KHR: String(rates) };
  }
  // from → dollars → to:  minor / 10^from / rateFrom × rateTo × 10^to
  const numerator = BigInt(minor) * rateOf(rates, to) * 10n ** BigInt(CURRENCY_INFO[to].minorDigits);
  const denominator = rateOf(rates, from) * 10n ** BigInt(CURRENCY_INFO[from].minorDigits);
  return toSafeNumber(divideRoundHalfAwayFromZero(numerator, denominator));
}

export function divideRoundHalfAwayFromZero(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n !== denominator < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const quotient = (n * 2n + d) / (d * 2n);
  return negative ? -quotient : quotient;
}
